import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { DocumentChip } from "@/profitability/document-chip";
import { GoalChecks } from "@/profitability/goal-checks";
import { profitabilityNewRunPath } from "@/profitability/routes";
import { SETTINGS_CONTROLS } from "@/profitability/settings/control-classes";
import { ToleranceField } from "@/profitability/settings/tolerance-field";
import type { GoalTolerance } from "@/profitability/settings/types";
import { toleranceLabel } from "@/profitability/settings/values";
import type { ProfitabilityReport } from "@/profitability/types";
import { SettingsGroup, SettingsRow } from "./settings-group";
import type { TemplateSettingsState } from "./use-settings-state";

export const matchRule = (tolerance: GoalTolerance, currency: string) =>
  toleranceLabel(tolerance, currency).replace(/^within/, "Within");
export const MatchingGroup = ({
  state,
  report,
}: {
  state: TemplateSettingsState;
  report: ProfitabilityReport;
}) => {
  const currency = useReportCurrency();
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const { settings, editable, save } = state;
  return (
    <SettingsGroup
      title="Reference checks"
      description="Compare a report with the expected-results file uploaded for that period."
    >
      <SettingsRow
        label="Expected results"
        value={
          report.goal_documents.length ? (
            <Stack direction="row" gap={2} wrap>
              {report.goal_documents.map((document) => (
                <DocumentChip key={document.id} document={document} />
              ))}
            </Stack>
          ) : (
            "No reference file was used to build this agent."
          )
        }
        hint="You can add expected results when uploading a report."
        action={
          editable && (
            <Button
              variant="outline"
              size="sm"
              render={<Link to={profitabilityNewRunPath(report.id)} />}
            >
              Upload report
            </Button>
          )
        }
      />
      <SettingsRow
        label="Allowed difference"
        value={
          settings ? (
            matchRule(settings.goal_tolerance, currency)
          ) : (
            <Skeleton
              role="status"
              aria-label="Loading allowed difference"
              className="h-5 w-32"
            />
          )
        }
        action={
          settings &&
          editable && (
            <Button
              variant="outline"
              size="sm"
              aria-label="Edit allowed difference"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          )
        }
      />
      {report.checks?.published && (
        <Stack className="p-4">
          <details>
            <summary className="cursor-pointer text-sm">
              View initial comparison
            </summary>
            <Stack className="pt-3">
              <GoalChecks checks={report.checks.published} />
            </Stack>
          </details>
        </Stack>
      )}
      {settings && editing && (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!pending) setEditing(next);
          }}
        >
          <DialogContent
            className={cn(
              "max-h-[calc(100dvh-2rem)] max-w-md overflow-y-auto overscroll-contain",
              SETTINGS_CONTROLS,
            )}
          >
            <DialogHeader>
              <DialogTitle>Allowed difference</DialogTitle>
              <DialogDescription>
                Sets the tolerance for future comparisons with expected results.
              </DialogDescription>
            </DialogHeader>
            <ToleranceField
              tolerance={settings.goal_tolerance}
              editable
              onSave={async (goal_tolerance) => {
                setPending(true);
                try {
                  await save({ goal_tolerance });
                } finally {
                  setPending(false);
                }
              }}
              onCancel={() => setEditing(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </SettingsGroup>
  );
};
